// Copy `text` to the clipboard, resilient to the contexts this embeddable
// widget actually runs in (Trello #856).
//
// The async Clipboard API (`navigator.clipboard.writeText`) is preferred, but
// it fails in exactly the embed cases:
//   - Chrome rejects it with `NotAllowedError` inside a cross-origin iframe
//     (document-not-focused / Permissions-Policy) even when `clipboard-write`
//     is delegated — Firefox is permissive, so the bug is "Chrome only";
//   - on an http:// host page `navigator.clipboard` is `undefined` entirely
//     (secure-context-gated), so the pre-fix
//     `navigator.clipboard.writeText(...)` threw a TypeError reading
//     `.writeText` OF undefined. (Reading an undefined *property* does not
//     throw; an earlier revision of this comment was wrong about that.)
//
// In both cases fall back to a synchronous `document.execCommand('copy')` off
// an off-screen <textarea>, which works inside the click's user gesture without
// the secure-context / focus constraints the async API enforces. The rejection
// is delivered from the browser process in a LATER TASK — not a microtask of
// the click task, as an earlier revision of this comment claimed — and the ~5s
// transient-activation window is still open there, verified in a real
// cross-origin embed.
//
// Returns a Promise that resolves on success (either path) and rejects when
// both paths fail, when there is nothing to copy, or when the async API neither
// resolves nor rejects within WRITE_TEXT_TIMEOUT_MS. On the
// async-then-fallback-fails path it rejects with the ORIGINAL writeText error
// so callers can still classify it (e.g. Menu.vue's Faro NotAllowedError
// counter).

// `writeText` can hang indefinitely — most reproducibly on Windows when another
// application holds the clipboard lock. Without a bound the returned promise
// never settles: no success tick, no failure alert, and the synchronous
// fallback that WOULD have worked is never tried. 1s is comfortably inside the
// ~5s transient-activation window, and a duplicate write of the same text on a
// late resolve is harmless.
const WRITE_TEXT_TIMEOUT_MS = 1000;

// The rejection reason for "we were given nothing to copy". It is a DATA
// condition, not a clipboard failure, and callers must be able to tell the two
// apart: both share controls used to turn every rejection into "Please check
// your browser's clipboard settings", blaming a listener's browser for an
// episode that simply has no shortUrl (Phase 2). A code, not a message match —
// a message is prose and gets reworded.
export const NOTHING_TO_COPY = 'NOTHING_TO_COPY';

const execCommandCopy = (text) => {
  if (
    typeof document === 'undefined'
    || typeof document.execCommand !== 'function'
    || !document.body
  ) {
    return false;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // readonly avoids a mobile keyboard popping up; fixed + off-screen keeps it
  // out of the layout while still selectable (display:none is not).
  // aria-hidden + tabindex=-1 keep it out of the a11y tree and the tab order
  // for the frame it exists.
  textarea.setAttribute('readonly', '');
  textarea.setAttribute('aria-hidden', 'true');
  textarea.setAttribute('tabindex', '-1');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';

  // Preserve any existing selection so the copy doesn't discard what the user
  // had highlighted. Inside a cross-origin embed this is the iframe's OWN
  // selection (the host page's is unreachable by design), so it mostly matters
  // for a same-origin / top-level embed. Fully guarded — selection bookkeeping
  // must never break the copy itself.
  let savedRange = null;
  try {
    const sel = typeof document.getSelection === 'function' ? document.getSelection() : null;
    if (sel && sel.rangeCount > 0) savedRange = sel.getRangeAt(0);
  } catch (e) { /* no usable selection API */ }

  // `textarea.select()` moves focus to the textarea, so without restoring it a
  // keyboard user lands on <body> after the copy and Tab restarts from the top
  // of the document.
  //
  // ⚠️ THIS COMMENT USED TO SAY THE v4 ROUTE WAS UNREACHABLE — "v4's copy control is a
  // click-only <a> with no href/tabindex (a pre-existing a11y gap), so any future fix
  // there would make this live". D-233 (A2) IS that fix: `Controls/Menu`'s Copy link is
  // a real `<button>` as of 2026-09-08, so both v3 and v4 reach this restore, and it is
  // covered from v4 by `test/unit-vue/a11y-v4-controls-reachable.spec.js`. Left in place
  // rather than deleted because the reasoning — why the restore exists at all — is the
  // part worth keeping.
  const previouslyFocused = document.activeElement;

  document.body.appendChild(textarea);
  let ok = false;
  try {
    if (typeof textarea.select === 'function') textarea.select();
    if (typeof textarea.setSelectionRange === 'function') {
      // Read the node's own value, not the outer parameter — identical today,
      // but the coupling could break silently under a future edit.
      textarea.setSelectionRange(0, textarea.value.length);
    }
    ok = document.execCommand('copy') === true;
  } catch (e) {
    ok = false;
  } finally {
    // MUST stay in `finally`: if execCommand throws, a textarea left behind
    // accumulates one orphan node per click, without bound.
    document.body.removeChild(textarea);
    if (savedRange) {
      try {
        const sel = document.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(savedRange);
        }
      } catch (e) { /* restore is best-effort */ }
    }
    try {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    } catch (e) { /* focus restore is best-effort */ }
  }
  return ok;
};

const fallbackOrReject = (value, error) => {
  if (execCommandCopy(value)) return Promise.resolve();
  return Promise.reject(error);
};

// eslint-disable-next-line import/prefer-default-export
export const copyToClipboard = (text) => {
  // Nothing to copy is a FAILURE, not a success. `app.vue` builds its
  // empty/malformed-loadData placeholder episode with `shortUrl: ''`, and
  // reporting "Link copied ✓" while the clipboard is untouched is the exact
  // "user believes they copied and pastes nothing" defect this util exists to
  // prevent. (Pre-fix, `writeText(undefined)` at least copied the literal
  // string "undefined", which a user would see and report.)
  if (text === null || text === undefined || text === '') {
    const nothing = new Error('nothing to copy');
    nothing.code = NOTHING_TO_COPY;
    return Promise.reject(nothing);
  }
  const value = String(text);

  // The ENTIRE body is guarded, the capability probe included: reading
  // `navigator.clipboard` can itself throw when it is an accessor installed by
  // a privacy extension or a hardened/enterprise build. Before this wrapper,
  // such a throw escaped `copyToClipboard` synchronously — past its own
  // fallback AND past the caller's `.catch` — leaving the user with no feedback
  // and nothing copied, in precisely the hostile environment the fallback
  // exists for.
  try {
    const hasAsyncApi = typeof navigator !== 'undefined'
      && navigator.clipboard
      && typeof navigator.clipboard.writeText === 'function';

    if (hasAsyncApi) {
      // A non-thenable return makes `.then` throw, caught below.
      const written = navigator.clipboard.writeText(value);
      return Promise.race([
        written.then(() => undefined),
        new Promise((resolve, reject) => {
          setTimeout(() => {
            reject(new Error('clipboard writeText timed out'));
          }, WRITE_TEXT_TIMEOUT_MS);
        }),
      ]).catch((error) => fallbackOrReject(value, error));
    }

    // No async Clipboard API at all (http:// embed / old browser).
    const unavailable = new Error('Clipboard API unavailable and execCommand copy failed');
    return fallbackOrReject(value, unavailable);
  } catch (syncError) {
    // Some engines throw synchronously — a SecurityError from the
    // `navigator.clipboard` getter, or from writeText itself. Route through the
    // fallback, preserving the original error's identity on total failure.
    return fallbackOrReject(value, syncError);
  }
};
